import React, { useEffect, useRef } from 'react';

/** Lightweight HTML5 canvas signature pad. Returns base64 PNG data URL via onChange. */
export default function SignaturePad({ width = 600, height = 200, onChange, value }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cv.width, cv.height);
    ctx.strokeStyle = '#0b1220';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = value;
    }
  }, [value]);

  const pos = (e) => {
    const cv = ref.current;
    const rect = cv.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t ? t.clientX : e.clientX) - rect.left;
    const y = (t ? t.clientY : e.clientY) - rect.top;
    return { x: (x / rect.width) * cv.width, y: (y / rect.height) * cv.height };
  };

  const start = (e) => { drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault?.();
    const p = pos(e);
    const ctx = ref.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange?.(ref.current.toDataURL('image/png'));
  };

  const clear = () => {
    const cv = ref.current;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cv.width, cv.height);
    onChange?.(null);
  };

  return (
    <div>
      <canvas
        ref={ref}
        width={width}
        height={height}
        data-testid="signature-canvas"
        className="signature-canvas w-full"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      <button type="button" data-testid="signature-clear-button" onClick={clear} className="mt-2 text-xs text-slate-500 hover:text-[#17352D] underline">Clear signature</button>
    </div>
  );
}
